/** @format */

import { Checkbox, FormControlLabel, TextField, Typography } from '@material-ui/core';
import React, { useState } from 'react';
import CustomTextButtom from '../../components/Buttons/CustomTextButton';
import GridContainer from '../../components/Grid/GridContainer';
import GridItem from '../../components/Grid/GridItem';
import Datetime from 'react-datetime';
import moment, { Moment } from 'moment';
import { ItemLoaders } from '../../core/loaders/ItemLoaders';
import { FormGeneralHelper } from '../../helpers/FormGeneral';
import { useDispatch } from 'react-redux';
import { numberRangeConstants } from '../../modules/numberRange/reducers';
import { DateHelper } from '../../helpers/DateHelper';
import I18n from '../../components/Translate/I18n';
import { NumberRange, NumberRangeInitial } from '../../interfaces/platform/NumberRange';
import { NotificationHelper } from '../../helpers/NotificationHelper';

interface NumberRangeEditFormInterface {
    platformResellerId: string;
    activeNumberRange: NumberRange | null;
}

const initialState: NumberRangeInitial = {
    numberStart: null,
    numberEnd: null,
    createdAt: moment(),
    activatedAt: moment(),
    platformResellerId: null,
};

const NumberRangeEditForm = (props: NumberRangeEditFormInterface): React.ReactElement => {
    const dispatch = useDispatch();
    const [oneNumberSelected, setOneNumberSelected] = useState<boolean>(false);
    const [activationDate, setActivationDate] = useState<Moment>(moment());
    const [entity, setEntity] = useState<NumberRange | NumberRangeInitial>(props.activeNumberRange ? props.activeNumberRange : initialState);
    const [entityEdited, setEntityEdited] = useState<boolean>(false);
    const handleOneNumberSelected = (event: any): void => {
        setOneNumberSelected(event.target.checked);
        setEntity({
            ...entity,
            numberEnd: null,
        });
    };

    const activationDateTimeChange = (value: Moment | string): void => {
        setEntity({
            ...entity,
            activatedAt: value,
        });
        setEntityEdited(true);
    };

    const saveNumberRanges = (): void => {
        const itemLoaders = new ItemLoaders(dispatch);

        const finalEntity: NumberRangeInitial = {
            ...entity,
            platformResellerId: parseInt(props.platformResellerId),
        };

        const valid = checkNumbersLength(finalEntity);

        if (!valid) {
            return;
        }

        if (entityEdited && props.activeNumberRange) {
            itemLoaders.putWithCallback(
                FormGeneralHelper.getUrl(`/number_ranges/${props.activeNumberRange ? props.activeNumberRange.id : null}`, 'platform'),
                numberRangeConstants.NUMBER_RANGE_EDIT,
                finalEntity,
                {
                    onSuccess: () => {
                        NotificationHelper.notifySaveSuccess(dispatch);
                        setEntityEdited(false);
                    },
                    onError: (error: any) => {
                        NotificationHelper.notifyError(error.message.message, dispatch);
                    },
                },
                {
                    propagateDataToCallback: true,
                    dispatchAfterCallback: false,
                    dispatchErrorAfterCallback: true,
                }
            );
        } else {
            itemLoaders.postWithCallback(
                FormGeneralHelper.getUrl('/number_ranges', 'platform'),
                numberRangeConstants.NUMBER_RANGE_ADD,
                finalEntity,
                {
                    onSuccess: () => {
                        NotificationHelper.notifySaveSuccess(dispatch);
                        setEntityEdited(false);
                    },
                    onError: (error: any) => {
                        NotificationHelper.notifyError(error.message.message, dispatch);
                    },
                },
                {
                    propagateDataToCallback: true,
                    dispatchAfterCallback: false,
                    dispatchErrorAfterCallback: true,
                }
            );
        }
    };

    const checkNumbersLength = (entity: NumberRangeInitial): boolean => {
        if (entity.numberEnd && entity.numberEnd.length !== 11) {
            NotificationHelper.notifyError('number_range:e.length_error', dispatch);
            return false;
        }
        if (entity.numberStart && entity.numberStart.length !== 11) {
            NotificationHelper.notifyError('number_range:e.length_error', dispatch);
            return false;
        }

        if (entity.numberEnd && !parseInt(entity.numberEnd)) {
            NotificationHelper.notifyError('number_range:e.not_number', dispatch);
            return false;
        }
        if (entity.numberStart && !parseInt(entity.numberStart)) {
            NotificationHelper.notifyError('number_range:e.not_number', dispatch);
            return false;
        }
        return true;
    };

    const handleTextFieldChange = (event: { target: { name: string; value: string } }): void => {
        setEntity({
            ...entity,
            [event.target.name]: event.target.value,
        });
        setEntityEdited(true);
    };

    return (
        <>
            <GridContainer style={{ margin: '20px 0' }}>
                <GridItem xs={12}>
                    <FormControlLabel
                        control={<Checkbox checked={oneNumberSelected} onChange={handleOneNumberSelected} name="oneNumber" />}
                        label={<I18n trans={'number_range:l.insert_only_one'} />}
                    />
                </GridItem>
                <GridItem xs={12} style={{ margin: '20px 0' }}>
                    <GridContainer>
                        <GridItem xs={12}>
                            <Typography variant={'body1'}>
                                {oneNumberSelected ? <I18n trans={'number_range:l.one_number'} /> : <I18n trans={'number_range:l.number_range'} />}
                            </Typography>
                        </GridItem>
                        <GridItem xs={4}>
                            <TextField
                                label={oneNumberSelected ? <I18n trans={'number_range:l.number'} /> : <I18n trans={'number_range:l.from_number'} />}
                                name={'numberStart'}
                                value={entity.numberStart}
                                onChange={handleTextFieldChange}
                            />
                        </GridItem>
                        <GridItem xs={4}>
                            {!oneNumberSelected && (
                                <TextField
                                    label={<I18n trans={'number_range:l.to_number'} />}
                                    name={'numberEnd'}
                                    value={entity.numberEnd}
                                    onChange={handleTextFieldChange}
                                />
                            )}
                        </GridItem>
                    </GridContainer>
                </GridItem>

                <GridItem xs={12} style={{ margin: '20px 0' }}>
                    <GridItem xs={12}>
                        <Typography variant={'body1'}>
                            <I18n trans={'number_range:l.activation_date'} />
                        </Typography>
                    </GridItem>
                    <GridItem xs={4}>
                        <Datetime
                            locale="pl"
                            timeFormat={false}
                            defaultValue={DateHelper.formatYYYYMMDD(entity.activatedAt)}
                            dateFormat={'YYYY-MM-DD'}
                            onChange={activationDateTimeChange}
                        />
                    </GridItem>
                </GridItem>
            </GridContainer>
            <GridContainer>
                <GridItem xs={12}>
                    <CustomTextButtom callback={saveNumberRanges} callbackParam={{}} label="Zapisz zakres" />
                </GridItem>
            </GridContainer>
        </>
    );
};

export default NumberRangeEditForm;
